import React, { useState, useEffect, useCallback } from 'react';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  BellAlertIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CpuChipIcon,
  CircleStackIcon,
  ServerStackIcon,
  ArrowPathIcon,
  UserPlusIcon,
  ChatBubbleLeftRightIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import {
  notifyAlertCountsChanged,
  PageTitle,
  PageWithFilters,
  FilterColumn,
  FilterSection,
  FilterCheck,
  useFilterColumn,
} from '@trinetra/shared';
import api from '../services/api.service';

// ─── Types ─────────────────────────────────────────────────────

interface AlertEvent {
  id: number;
  node_id: string;
  node_name: string | null;
  alert_type: string;
  severity: string;
  value: number | null;
  threshold: number | null;
  message: string | null;
  active: boolean;
  acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  acknowledged_at: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_by_name: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  cancelled: boolean;
  cancelled_by_name: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  /** When it started (the first high check). */
  created_at: string;
  /** 1 + every iteration of the same incident (still high after another cooldown). */
  occurrences?: number;
  last_at?: string | null;
  /** Which machine of the site — or the cloud server. */
  machine?: string | null;
  /** 'auto' = recovered by itself. */
  resolved_how?: string | null;
}

interface AlertSummary {
  unacknowledged_count: number;
  active_count: number;
  active_critical: number;
  active_warning: number;
}

// ─── Service error domain (separate from resource alerts + AI analytics) ──
interface ServiceError {
  id: number;
  node_id: string;
  node_name: string | null;
  service: string;
  error_code: string;
  component: string | null;
  severity: string;
  message: string;
  details: any;
  occurrence_count: number;
  /** The host the failing service runs on. */
  machine?: string | null;
  /** 'auto' = the service was running again. */
  resolved_how?: string | null;
  resolved_at?: string | null;
  active: boolean;
  acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_by_name: string | null;
  resolved_by_name: string | null;
  last_seen: string;
  created_at: string;
}

interface CloudUser {
  id: string;
  name: string | null;
  email: string;
}

// Which system-alert domain a row belongs to (drives the API base path).
type AlertSource = 'service_error' | 'alert_event';
const SOURCE_BASE: Record<AlertSource, string> = {
  service_error: '/system-alerts/errors',
  alert_event: '/alert-system/events',
};

interface ServiceRollup {
  service: string;
  active: number;
  critical: number;
  last_seen: string;
}

// ─── Helpers ────────────────────────────────────────────────────

const ALERT_TYPE_LABEL: Record<string, string> = {
  cpu_high: 'CPU Usage',
  memory_high: 'Memory Usage',
  disk_high: 'Disk Usage',
  node_offline: 'Site Offline',
};

/** A site's own CPU / memory / disk rule is filed per machine: 'cpu_high@<machine>'. */
const alertTypeLabel = (type: string): string => {
  const [base, machine] = type.split('@');
  const label = ALERT_TYPE_LABEL[base] || base;
  return machine ? `${label} · ${machine}` : label;
};
const alertTypeIconOf = (type: string): React.ReactNode => alertTypeIcon[type.split('@')[0]];
/** Exactly which machine: its own column, else the part of 'cpu_high@<machine>' after the @. */
const machineOf = (e: { machine?: string | null; alert_type?: string }): string | null => e.machine || (e.alert_type?.split('@')[1] ?? null);
const siteOf = (e: { node_id: string; node_name: string | null }) => (e.node_id === 'cloud-self' ? 'Cloud server' : e.node_name || e.node_id);
/** "42 min", "3 h 5 min", "2 d 4 h" between two times. */
function lasted(from: string | null | undefined, to: string | null | undefined): string {
  if (!from) return '';
  const ms = Math.max(0, (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime());
  const m = Math.round(ms / 60000);
  if (m < 60) return `${Math.max(1, m)} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h${m % 60 ? ` ${m % 60} min` : ''}`;
  return `${Math.floor(h / 24)} d${h % 24 ? ` ${h % 24} h` : ''}`;
}

const alertTypeIcon: Record<string, React.ReactNode> = {
  cpu_high: <CpuChipIcon className="h-4 w-4" />,
  memory_high: <ServerStackIcon className="h-4 w-4" />,
  disk_high: <CircleStackIcon className="h-4 w-4" />,
  node_offline: <XMarkIcon className="h-4 w-4" />,
};

const sevColors: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-500',
  error: 'bg-orange-500/15 text-orange-500',
  warning: 'bg-amber-500/15 text-amber-500',
};

function formatDateTime(v: string | null) {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ─── Shared style tokens (match AlertInvestigationPage) ────────
const cardCls = 'rounded-xl border border-border bg-card shadow-card';
const selectCls = 'h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground focus:border-brand focus:outline-none';
const fieldCls = 'w-full h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none';
const badgeCls = 'px-1.5 py-0.5 text-[10px] font-bold uppercase rounded';
const thCls = 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';
const labelCls = 'block text-[12px] font-medium text-muted-foreground mb-1';
const primaryBtnCls = 'h-9 px-4 rounded-lg bg-brand text-white text-[13px] font-medium hover:bg-brand/90 disabled:opacity-50 transition-colors whitespace-nowrap';
const ghostBtnCls = 'h-9 px-4 rounded-lg border border-border text-[13px] text-foreground hover:bg-muted disabled:opacity-50 transition-colors whitespace-nowrap';

const SeverityBadge = ({ severity }: { severity: string }) => (
  <span className={`${badgeCls} ${sevColors[severity] || 'bg-gray-500/15 text-gray-500'}`}>{severity}</span>
);

const StatusBadge = ({ active, resolvedAt, cancelled, how }: { active: boolean; resolvedAt?: string | null; cancelled?: boolean; how?: string | null }) =>
  active ? (
    <span className={`${badgeCls} bg-red-500/15 text-red-500 animate-pulse`}>Active</span>
  ) : cancelled ? (
    <span className={`${badgeCls} bg-muted text-muted-foreground`}>Cancelled</span>
  ) : resolvedAt !== undefined ? (
    <span className={`${badgeCls} bg-emerald-500/15 text-emerald-500`} title={resolvedAt ? `at ${formatDateTime(resolvedAt)}` : undefined}>{how === 'auto' ? 'Recovered' : 'Resolved'}</span>
  ) : null;

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════

const TABS = [
  { key: 'events', label: 'System alerts', icon: BellAlertIcon },
  { key: 'serviceErrors', label: 'System errors', icon: ServerStackIcon },
] as const;
// When and to whom these are sent — rules, recipients, channels — is set on Alerts — Config &
// Actions (/alert-actions), per site and for the cloud itself.

/**
 * `nodeId` pins the page to one node's records — the user menu's System Alerts opens it for the
 * cloud itself ('cloud-self'). Without it, every site the user can see (the header meter's page).
 */
export default function SystemAlertsPage({ nodeId, title = 'System Alerts', subtitle = 'Sites offline, CPU / memory / disk, and per-service errors' }: {
  nodeId?: string; title?: string; subtitle?: string;
} = {}) {
  const [tab, setTab] = useState<'events' | 'serviceErrors'>('events');
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [serviceErrors, setServiceErrors] = useState<ServiceError[]>([]);
  const [serviceRollup, setServiceRollup] = useState<ServiceRollup[]>([]);
  const [svcFilter, setSvcFilter] = useState<{ active?: boolean; severity?: string; service?: string }>({ active: true });
  const [loading, setLoading] = useState(true);
  const [eventsFilter, setEventsFilter] = useState<{ active?: boolean; severity?: string }>({ active: true });
  const [users, setUsers] = useState<CloudUser[]>([]);
  const [assignFor, setAssignFor] = useState<{ source: AlertSource; id: number; title: string } | null>(null);
  const [activityFor, setActivityFor] = useState<{ source: AlertSource; id: number; title: string } | null>(null);
  const [detailFor, setDetailFor] = useState<{ source: AlertSource; row: any; title: string } | null>(null);
  // Resolve / cancel of an alert event opens a small note/reason prompt first.
  const [eventActionFor, setEventActionFor] = useState<{ id: number; kind: 'resolve' | 'cancel' } | null>(null);
  const { nodes } = useNode();
  const { hasPermission } = useAuth();
  // Acknowledge / assign / resolve / cancel / reopen mutate service errors and
  // alert events; the backend gates them on system_alert.edit, so hide when absent.
  const canEdit = hasPermission('system_alert.edit');

  // Users for the assign dropdown; needs users.view — hide assign if forbidden.
  useEffect(() => {
    api.get('/users')
      .then((res) => setUsers(res.data?.data || []))
      .catch(() => setUsers([]));
  }, []);

  const fetchSummary = useCallback(async () => {
    // The summary counts every visible site — it cannot be narrowed to one node.
    if (nodeId) { setSummary(null); return; }
    try {
      const res = await api.get('/alert-system/summary');
      setSummary(res.data?.data || res.data);
    } catch { /* ignore */ }
  }, [nodeId]);

  const fetchEvents = useCallback(async () => {
    try {
      const params: any = { limit: 100 };
      if (nodeId) params.node_id = nodeId;
      if (eventsFilter.active !== undefined) params.active = eventsFilter.active;
      if (eventsFilter.severity) params.severity = eventsFilter.severity;
      const res = await api.get('/alert-system/events', { params });
      const data = res.data?.data || res.data;
      setEvents(data.rows || data);
    } catch { /* ignore */ }
  }, [eventsFilter, nodeId]);

  // ── Service errors (system/service domain) ──
  const fetchServiceErrors = useCallback(async () => {
    try {
      const params: any = { limit: 200 };
      if (nodeId) params.node_id = nodeId;
      if (svcFilter.active !== undefined) params.active = svcFilter.active;
      if (svcFilter.severity) params.severity = svcFilter.severity;
      if (svcFilter.service) params.service = svcFilter.service;
      const res = await api.get('/system-alerts/errors', { params });
      const data = res.data?.data || res.data;
      setServiceErrors(data.rows || data);
    } catch { /* ignore */ }
  }, [svcFilter, nodeId]);

  const fetchServiceRollup = useCallback(async () => {
    try {
      const res = await api.get('/system-alerts/services', { params: nodeId ? { node_id: nodeId } : {} });
      setServiceRollup(res.data?.data || res.data || []);
    } catch { /* ignore */ }
  }, [nodeId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchEvents(), fetchServiceErrors(), fetchServiceRollup()]);
      setLoading(false);
    };
    load();
  }, [fetchSummary, fetchEvents, fetchServiceErrors, fetchServiceRollup]);

  useEffect(() => { fetchEvents(); }, [eventsFilter, fetchEvents]);
  useEffect(() => { fetchServiceErrors(); }, [svcFilter, fetchServiceErrors]);

  // Poll for real-time updates every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSummary();
      fetchEvents();
      fetchServiceErrors();
      fetchServiceRollup();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchSummary, fetchEvents, fetchServiceErrors, fetchServiceRollup]);

  const refreshAll = () => {
    fetchSummary();
    fetchEvents();
    fetchServiceErrors();
    fetchServiceRollup();
  };

  const handleAcknowledge = async (id: number) => {
    try {
      await api.patch(`/alert-system/events/${id}/acknowledge`);
      fetchEvents();
      fetchSummary();
    } catch { /* ignore */ }
  };

  const handleServiceAck = async (id: number) => {
    try {
      await api.patch(`/system-alerts/errors/${id}/acknowledge`);
      fetchServiceErrors();
      // The title bar counts UNACKNOWLEDGED service errors, so this changed it — say so
      // rather than leaving the badge on a stale number for up to a minute (alertCountsBus).
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleServiceResolve = async (id: number) => {
    try {
      await api.post(`/system-alerts/errors/${id}/resolve`);
      fetchServiceErrors();
      fetchServiceRollup();
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleServiceReopen = async (id: number) => {
    try {
      await api.post(`/system-alerts/errors/${id}/reopen`);
      fetchServiceErrors();
      fetchServiceRollup();
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleAssign = async (target: { source: AlertSource; id: number }, assignee: CloudUser) => {
    try {
      await api.patch(`${SOURCE_BASE[target.source]}/${target.id}/assign`, {
        assignee_id: assignee.id,
        assignee_name: assignee.name || assignee.email,
      });
      if (target.source === 'service_error') fetchServiceErrors(); else fetchEvents();
      notifyAlertCountsChanged();
    } catch { /* ignore */ }
  };

  const handleResolveEvent = async (id: number, note: string) => {
    try {
      await api.post(`/alert-system/events/${id}/resolve`, { note });
      fetchEvents();
      fetchSummary();
    } catch { /* ignore */ }
  };

  const handleCancelEvent = async (id: number, reason: string) => {
    try {
      await api.post(`/alert-system/events/${id}/cancel`, { reason });
      fetchEvents();
      fetchSummary();
    } catch { /* ignore */ }
  };

  const svcUnacked = serviceErrors.filter((e) => e.active && !e.acknowledged).length;
  const tabBadge: Record<string, number> = {
    events: nodeId ? events.filter((e) => e.active && !e.acknowledged).length : summary?.unacknowledged_count || 0,
    serviceErrors: svcUnacked,
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-56 rounded-lg bg-muted animate-pulse" />
        <div className="h-10 rounded-lg bg-muted animate-pulse" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PageTitle title={title} subtitle={subtitle}>
        {summary && (
          <>
            {summary.active_critical > 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium bg-red-500/15 text-red-500">
                <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                {summary.active_critical} Critical
              </span>
            )}
            {summary.active_warning > 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium bg-amber-500/15 text-amber-500">
                <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                {summary.active_warning} Warning
              </span>
            )}
            {summary.active_count === 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium bg-emerald-500/15 text-emerald-500">
                <CheckCircleIcon className="h-3.5 w-3.5" />
                All Clear
              </span>
            )}
          </>
        )}
        <button
          data-tour="system-alerts-refresh"
          onClick={refreshAll}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </PageTitle>

      {/* ── Tabs ── */}
      <div data-tour="system-alerts-tabs" className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.key
              ? 'border-brand text-brand'
              : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
            {(tabBadge[t.key] || 0) > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white">
                {tabBadge[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      {tab === 'events' ? (
        <EventsPanel
          events={events}
          filter={eventsFilter}
          setFilter={setEventsFilter}
          onAcknowledge={handleAcknowledge}
          onResolve={(e: AlertEvent) => setEventActionFor({ id: e.id, kind: 'resolve' })}
          onCancel={(e: AlertEvent) => setEventActionFor({ id: e.id, kind: 'cancel' })}
          canEdit={canEdit}
          canAssign={canEdit && users.length > 0}
          onAssign={(e: AlertEvent) => setAssignFor({ source: 'alert_event', id: e.id, title: alertTypeLabel(e.alert_type) })}
          onActivity={(e: AlertEvent) => setActivityFor({ source: 'alert_event', id: e.id, title: alertTypeLabel(e.alert_type) })}
          onRowClick={(e: AlertEvent) => setDetailFor({ source: 'alert_event', row: e, title: alertTypeLabel(e.alert_type) })}
        />
      ) : tab === 'serviceErrors' ? (
        <ServiceErrorsPanel
          errors={serviceErrors}
          rollup={serviceRollup}
          filter={svcFilter}
          setFilter={setSvcFilter}
          onAcknowledge={handleServiceAck}
          onResolve={handleServiceResolve}
          onReopen={handleServiceReopen}
          canEdit={canEdit}
          canAssign={canEdit && users.length > 0}
          onAssign={(e: ServiceError) => setAssignFor({ source: 'service_error', id: e.id, title: `${e.service} · ${e.error_code}` })}
          onActivity={(e: ServiceError) => setActivityFor({ source: 'service_error', id: e.id, title: `${e.service} · ${e.error_code}` })}
          onRowClick={(e: ServiceError) => setDetailFor({ source: 'service_error', row: e, title: `${e.service} · ${e.error_code}` })}
        />
      ) : null}

      {assignFor && (
        <AssignModal
          title={assignFor.title}
          users={users}
          onClose={() => setAssignFor(null)}
          onConfirm={(u: CloudUser) => {
            handleAssign(assignFor, u);
            setAssignFor(null);
          }}
        />
      )}
      {activityFor && (
        <ActivityModal
          title={activityFor.title}
          source={activityFor.source}
          alertId={activityFor.id}
          onClose={() => setActivityFor(null)}
        />
      )}
      {detailFor && (
        <DetailModal
          source={detailFor.source}
          row={detailFor.row}
          title={detailFor.title}
          nodes={nodes}
          onClose={() => setDetailFor(null)}
        />
      )}
      {eventActionFor && (
        <NoteModal
          kind={eventActionFor.kind}
          onClose={() => setEventActionFor(null)}
          onConfirm={(text: string) => {
            if (eventActionFor.kind === 'resolve') handleResolveEvent(eventActionFor.id, text);
            else handleCancelEvent(eventActionFor.id, text);
            setEventActionFor(null);
          }}
        />
      )}
    </div>
  );
}

/** Status radios for a panel's filter column — same `active` flag the old dropdown set. */
function StatusFilter({ filter, setFilter }: { filter: { active?: boolean }; setFilter: (f: any) => void }) {
  const value = filter.active === undefined ? 'all' : filter.active ? 'active' : 'resolved';
  return (
    <FilterSection title="Status">
      <div data-tour="system-alerts-filters" className="space-y-0.5">
        {([['all', 'All Status'], ['active', 'Active'], ['resolved', 'Resolved']] as const).map(([k, label]) => (
          <FilterCheck
            key={k}
            radio
            checked={value === k}
            onClick={() => setFilter({ ...filter, active: k === 'all' ? undefined : k === 'active' })}
            label={label}
          />
        ))}
      </div>
    </FilterSection>
  );
}

function SeverityFilter({ filter, setFilter, options }: {
  filter: { severity?: string }; setFilter: (f: any) => void; options: [string, string][];
}) {
  return (
    <FilterSection title="Severity">
      <div className="space-y-0.5">
        <FilterCheck radio checked={!filter.severity} onClick={() => setFilter({ ...filter, severity: undefined })} label="All Severities" />
        {options.map(([k, label]) => (
          <FilterCheck key={k} radio checked={filter.severity === k} onClick={() => setFilter({ ...filter, severity: k })} label={label} />
        ))}
      </div>
    </FilterSection>
  );
}

// ═══════════════════════════════════════════════════════════════
//  EVENTS PANEL
// ═══════════════════════════════════════════════════════════════

function EventsPanel({
  events, filter, setFilter, onAcknowledge, onResolve, onCancel, canEdit, canAssign, onAssign, onActivity, onRowClick,
}: {
  events: AlertEvent[];
  filter: { active?: boolean; severity?: string };
  setFilter: (f: any) => void;
  onAcknowledge: (id: number) => void;
  onResolve: (e: AlertEvent) => void;
  onCancel: (e: AlertEvent) => void;
  canEdit: boolean;
  canAssign: boolean;
  onAssign: (e: AlertEvent) => void;
  onActivity: (e: AlertEvent) => void;
  onRowClick: (e: AlertEvent) => void;
}) {
  const filterCol = useFilterColumn('system-alerts');
  return (
    <PageWithFilters
      filters={(
        <FilterColumn
          open={filterCol.open}
          onToggle={filterCol.toggle}
          label="Alert filters"
          activeCount={(filter.active !== undefined ? 1 : 0) + (filter.severity ? 1 : 0)}
          onClearAll={() => setFilter({})}
        >
          <StatusFilter filter={filter} setFilter={setFilter} />
          <SeverityFilter
            filter={filter}
            setFilter={setFilter}
            options={[['critical', 'Critical'], ['warning', 'Warning']]}
          />
        </FilterColumn>
      )}
    >
      {/* ── Events Table ── */}
      <div data-tour="system-alerts-events" className={`${cardCls} overflow-hidden`}>
        <div className="overflow-x-auto">
          {events.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              No alert events found for the selected filters.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={thCls}>Alert</th>
                  <th className={thCls}>Description</th>
                  <th className={thCls}>Severity</th>
                  <th className={thCls}>Where</th>
                  <th className={thCls}>Times</th>
                  <th className={thCls}>Status</th>
                  <th className={thCls}>Assignee</th>
                  <th className={thCls}>Action</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr
                    key={event.id}
                    onClick={() => onRowClick(event)}
                    className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${event.active ? '' : 'opacity-60'}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${sevColors[event.severity] || 'bg-gray-500/15 text-gray-500'}`}>
                          {alertTypeIconOf(event.alert_type) || <BellAlertIcon className="h-4 w-4" />}
                        </span>
                        <span className="font-medium text-foreground">
                          {alertTypeLabel(event.alert_type)}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[320px]">
                      <span className="block truncate text-muted-foreground" title={event.message || undefined}>
                        {event.message || `${event.node_name || event.node_id} — ${event.value?.toFixed(1)}% (threshold: ${event.threshold}%)`}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {event.active
                          ? `Since ${formatDateTime(event.created_at)} · ${lasted(event.created_at, null)} so far`
                          : `${formatDateTime(event.created_at)} → ${formatDateTime(event.resolved_at || event.cancelled_at)} · lasted ${lasted(event.created_at, event.resolved_at || event.cancelled_at)}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SeverityBadge severity={event.severity} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="block text-foreground">{siteOf(event)}</span>
                      {machineOf(event) && <span className="block text-[11px] text-muted-foreground">{machineOf(event)}</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" title={event.last_at ? `Last seen ${formatDateTime(event.last_at)}` : undefined}>
                      {(event.occurrences || 1) > 1
                        ? <span className={`${badgeCls} bg-muted text-foreground`}>×{event.occurrences}</span>
                        : <span className="text-muted-foreground">1</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge active={event.active} resolvedAt={event.resolved_at} cancelled={event.cancelled} how={event.resolved_how} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" title={event.assigned_by_name ? `Assigned by ${event.assigned_by_name}` : undefined}>
                      {event.assigned_to ? (event.assigned_to_name || event.assigned_to) : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                      <span className="flex items-center gap-1.5">
                        {canEdit && event.active && !event.acknowledged ? (
                          <button
                            onClick={() => onAcknowledge(event.id)}
                            title="Acknowledge this alert"
                            className="h-7 px-2.5 rounded-md bg-brand/10 text-brand text-[11px] font-medium hover:bg-brand/20 transition-colors"
                          >
                            Acknowledge
                          </button>
                        ) : event.acknowledged ? (
                          <span className="text-[11px] text-muted-foreground">✓ Acked by {event.acknowledged_by_name || event.acknowledged_by}</span>
                        ) : null}
                        {canAssign && event.active && (
                          <button
                            onClick={() => onAssign(event)}
                            title="Assign to a user"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                          >
                            <UserPlusIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEdit && event.active && (
                          <>
                            <button
                              onClick={() => onResolve(event)}
                              title="Resolve this alert"
                              className="flex h-7 w-7 items-center justify-center rounded-md text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => onCancel(event)}
                              title="Cancel (dismiss) this alert"
                              className="flex h-7 w-7 items-center justify-center rounded-md text-red-500 hover:bg-red-500/10 transition-colors"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => onActivity(event)}
                          title="Activity & comments"
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PageWithFilters>
  );
}

// ═══════════════════════════════════════════════════════════════
//  SERVICE ERRORS PANEL  (system/service domain — NOT AI analytics)
// ═══════════════════════════════════════════════════════════════

function ServiceErrorsPanel({
  errors, rollup, filter, setFilter, onAcknowledge, onResolve, onReopen, canEdit, canAssign, onAssign, onActivity, onRowClick,
}: {
  errors: ServiceError[];
  rollup: ServiceRollup[];
  filter: { active?: boolean; severity?: string; service?: string };
  setFilter: (f: any) => void;
  onAcknowledge: (id: number) => void;
  onResolve: (id: number) => void;
  onReopen: (id: number) => void;
  canEdit: boolean;
  canAssign: boolean;
  onAssign: (e: ServiceError) => void;
  onActivity: (e: ServiceError) => void;
  onRowClick: (e: ServiceError) => void;
}) {
  // Host name arrives inside the freeform error `details` payload (not a column).
  // Exactly which machine: its own column (migration 031), else what the details say.
  const hostOf = (e: ServiceError): string | null =>
    e.machine || (e.details && (e.details.hostname || e.details.host)) || null;

  // Group the (already filtered) error list by service for display.
  const byService = errors.reduce<Record<string, ServiceError[]>>((acc, e) => {
    (acc[e.service] ||= []).push(e);
    return acc;
  }, {});
  const services = Object.keys(byService).sort();
  const filterCol = useFilterColumn('system-alerts');

  return (
    <PageWithFilters
      filters={(
        <FilterColumn
          open={filterCol.open}
          onToggle={filterCol.toggle}
          label="Error filters"
          activeCount={(filter.active !== undefined ? 1 : 0) + (filter.severity ? 1 : 0) + (filter.service ? 1 : 0)}
          onClearAll={() => setFilter({})}
        >
          <StatusFilter filter={filter} setFilter={setFilter} />
          <SeverityFilter
            filter={filter}
            setFilter={setFilter}
            options={[['critical', 'Critical'], ['error', 'Error'], ['warning', 'Warning']]}
          />
        </FilterColumn>
      )}
    >
      {/* ── Per-service summary grid (service-wise health) ── */}
      {rollup.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {rollup.map((s) => (
            <button
              key={s.service}
              onClick={() => setFilter({ ...filter, service: filter.service === s.service ? undefined : s.service })}
              className={`${cardCls} p-4 text-left transition-colors hover:bg-muted/30 ${
                filter.service === s.service ? 'ring-2 ring-brand' : ''
              } ${s.critical > 0 ? 'border-red-500/40' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium text-foreground truncate">{s.service}</span>
                <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${s.critical > 0 ? 'bg-red-500 animate-pulse' : 'bg-amber-400'}`} />
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-semibold text-foreground">{s.active}</span>
                <span className="text-[11px] text-muted-foreground">active</span>
                {s.critical > 0 && (
                  <span className="ml-auto text-[11px] font-medium text-red-500">{s.critical} critical</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                last: {s.last_seen ? formatDateTime(s.last_seen) : '—'}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* Service picked from a summary card — an active filter, so it stays beside the list. */}
      {filter.service && (
        <div>
          <button
            onClick={() => setFilter({ ...filter, service: undefined })}
            className="flex h-8 items-center gap-1 rounded-lg bg-brand/10 px-3 text-[12px] font-medium text-brand hover:bg-brand/20 transition-colors"
          >
            {filter.service}
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* ── Errors Table (grouped by service) ── */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="overflow-x-auto">
          {services.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              No service errors found for the selected filters.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={thCls}>Error Code</th>
                  <th className={thCls}>Message</th>
                  <th className={thCls}>Severity</th>
                  <th className={thCls}>Component</th>
                  <th className={thCls}>Where</th>
                  <th className={thCls}>Times</th>
                  <th className={thCls}>Last Seen</th>
                  <th className={thCls}>Status</th>
                  <th className={thCls}>Assignee</th>
                  <th className={thCls}>Action</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => (
                  <React.Fragment key={svc}>
                    <tr className="border-b border-border bg-muted/40">
                      <td colSpan={10} className="px-4 py-2">
                        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          <ServerStackIcon className="h-3.5 w-3.5" />
                          {svc}
                          <span className="font-normal normal-case">({byService[svc].length})</span>
                        </span>
                      </td>
                    </tr>
                    {byService[svc].map((e) => (
                      <tr
                        key={e.id}
                        onClick={() => onRowClick(e)}
                        className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${e.active ? '' : 'opacity-60'}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-muted text-foreground">{e.error_code}</span>
                        </td>
                        <td className="px-4 py-3 max-w-[320px]">
                          <span className="block truncate text-foreground" title={e.message}>{e.message}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <SeverityBadge severity={e.severity} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{e.component || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="block text-foreground">{siteOf(e)}</span>
                          {hostOf(e) && hostOf(e) !== e.node_name && <span className="block text-[11px] text-muted-foreground">{hostOf(e)}</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {e.occurrence_count > 1 ? (
                            <span className={`${badgeCls} bg-muted text-foreground`}>×{e.occurrence_count}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" title={e.acknowledged ? `Acked by ${e.acknowledged_by_name || e.acknowledged_by}` : undefined}>
                          {formatDateTime(e.last_seen)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {e.active ? (
                            <span className={`${badgeCls} bg-red-500/15 text-red-500 animate-pulse`}>Active</span>
                          ) : (
                            <span className={`${badgeCls} bg-emerald-500/15 text-emerald-500`} title={e.resolved_at ? `at ${formatDateTime(e.resolved_at)}` : undefined}>
                              {e.resolved_how === 'auto' ? 'Recovered' : 'Resolved'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" title={e.assigned_by_name ? `Assigned by ${e.assigned_by_name}` : undefined}>
                          {e.assigned_to ? (e.assigned_to_name || e.assigned_to) : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                          <span className="flex items-center gap-1.5">
                            {canEdit && e.active && !e.acknowledged && (
                              <button
                                onClick={() => onAcknowledge(e.id)}
                                title="Acknowledge this error"
                                className="h-7 px-2.5 rounded-md bg-brand/10 text-brand text-[11px] font-medium hover:bg-brand/20 transition-colors"
                              >
                                Acknowledge
                              </button>
                            )}
                            {e.active && e.acknowledged && (
                              <span className="text-[11px] text-muted-foreground">✓ {e.acknowledged_by_name || e.acknowledged_by}</span>
                            )}
                            {canEdit && e.active && (
                              <button
                                onClick={() => onResolve(e.id)}
                                title="Resolve this error"
                                className="h-7 px-2.5 rounded-md bg-emerald-500/10 text-emerald-500 text-[11px] font-medium hover:bg-emerald-500/20 transition-colors"
                              >
                                Resolve
                              </button>
                            )}
                            {!e.active && (
                              <>
                                {e.resolved_by_name && (
                                  <span className="text-[11px] text-muted-foreground">✓ Resolved by {e.resolved_by_name}</span>
                                )}
                                {canEdit && <button
                                  onClick={() => onReopen(e.id)}
                                  title="Reopen this error"
                                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                                >
                                  <ArrowUturnLeftIcon className="h-4 w-4" />
                                </button>}
                              </>
                            )}
                            {canAssign && e.active && (
                              <button
                                onClick={() => onAssign(e)}
                                title="Assign to a user"
                                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                              >
                                <UserPlusIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => onActivity(e)}
                              title="Activity & comments"
                              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                              <ChatBubbleLeftRightIcon className="h-4 w-4" />
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PageWithFilters>
  );
}

// ═══════════════════════════════════════════════════════════════
//  ASSIGN + ACTIVITY MODALS (shared by both system-alert domains)
// ═══════════════════════════════════════════════════════════════

function AssignModal({
  title, users, onClose, onConfirm,
}: {
  title: string;
  users: CloudUser[];
  onClose: () => void;
  onConfirm: (u: CloudUser) => void;
}) {
  const [assigneeId, setAssigneeId] = useState(users[0]?.id || '');
  const assignee = users.find((u) => u.id === assigneeId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-1">Assign alert</h2>
        <p className="text-[12px] text-muted-foreground mb-3 truncate">{title}</p>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={`${selectCls} w-full`}>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name || u.email}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className={ghostBtnCls}>Cancel</button>
          <button onClick={() => assignee && onConfirm(assignee)} disabled={!assignee} className={primaryBtnCls}>
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

interface SystemAlertAction {
  id: number;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  target_id: string | null;
  target_name: string | null;
  note: string | null;
  created_at: string;
}

const ACTION_LABEL: Record<string, string> = {
  acknowledged: 'acknowledged this alert',
  assigned: 'assigned this alert',
  resolved: 'resolved this alert',
  cancelled: 'cancelled this alert',
  reopened: 'reopened this alert',
  commented: 'commented',
};

/** The lifecycle activity list + comment box for one alert/error. Shared by the
 *  Activity modal and the row-detail modal. */
function ActivityFeed({ source, alertId }: { source: AlertSource; alertId: number }) {
  const [actions, setActions] = useState<SystemAlertAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${SOURCE_BASE[source]}/${alertId}/actions`);
      setActions(res.data?.data || res.data || []);
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, [source, alertId]);

  useEffect(() => { load(); }, [load]);

  const nameOf = (id: string | null, name: string | null) => name || id || 'Unknown';

  const postComment = async () => {
    const text = comment.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await api.post(`${SOURCE_BASE[source]}/${alertId}/comments`, { comment: text });
      setComment('');
      await load();
    } catch { /* ignore */ }
    finally { setPosting(false); }
  };

  return (
    <>
      <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Lifecycle log</h3>
      {loading ? (
        <p className="text-[12px] text-muted-foreground">Loading activity…</p>
      ) : actions.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">No activity yet.</p>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {actions.map((ac) => (
            <li key={ac.id} className="text-[13px]">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-foreground">
                  <span className="font-medium">{nameOf(ac.actor_id, ac.actor_name)}</span>
                  {' '}{ACTION_LABEL[ac.action] || ac.action}
                  {ac.action === 'assigned' && (
                    <> to <span className="font-medium">{nameOf(ac.target_id, ac.target_name)}</span></>
                  )}
                </span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{formatDateTime(ac.created_at)}</span>
              </div>
              {ac.note && (
                <p className="mt-0.5 rounded bg-muted/60 px-2 py-1 text-[12px] text-foreground whitespace-pre-wrap">{ac.note}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2 mt-4">
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && postComment()}
          placeholder="Add a comment…"
          className={fieldCls}
        />
        <button onClick={postComment} disabled={!comment.trim() || posting} className={primaryBtnCls}>
          {posting ? 'Posting…' : 'Comment'}
        </button>
      </div>
    </>
  );
}

function ActivityModal({
  title, source, alertId, onClose,
}: {
  title: string;
  source: AlertSource;
  alertId: number;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-lg font-semibold text-foreground">Activity</h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[12px] text-muted-foreground mb-3 truncate">{title}</p>
        <ActivityFeed source={source} alertId={alertId} />
      </div>
    </div>
  );
}

// ─── Row-detail modal (full record + lifecycle log) ─────────────
function DField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="text-[13px] text-foreground font-medium break-words">{value ?? '—'}</dd>
    </div>
  );
}

function DetailModal({
  source, row, title, nodes, onClose,
}: {
  source: AlertSource;
  row: any;
  title: string;
  nodes: any[];
  onClose: () => void;
}) {
  const nodeName = row.node_name || nodes.find((n: any) => n.nodeId === row.node_id)?.name || row.node_id;
  const host = row.details && (row.details.hostname || row.details.host);
  const assignee = row.assigned_to ? (row.assigned_to_name || row.assigned_to) : 'Unassigned';
  const isEvent = source === 'alert_event';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 backdrop-blur px-6 py-4 rounded-t-2xl">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-lg font-semibold text-foreground truncate">{title}</h2>
            <SeverityBadge severity={row.severity} />
            <StatusBadge active={row.active} resolvedAt={row.resolved_at ?? undefined} cancelled={row.cancelled} />
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-4">
          {row.message && <p className="text-[13px] text-foreground mb-4">{row.message}</p>}
          <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-5">
            <DField label="Node" value={host ? `${nodeName} · ${host}` : nodeName} />
            {isEvent ? (
              <>
                <DField label="Alert type" value={alertTypeLabel(row.alert_type)} />
                <DField label="Value / threshold" value={row.value != null ? `${Number(row.value).toFixed(1)}% / ${row.threshold}%` : '—'} />
                <DField label="Detected" value={formatDateTime(row.created_at)} />
              </>
            ) : (
              <>
                <DField label="Error code" value={<span className="font-mono">{row.error_code}</span>} />
                <DField label="Service / component" value={[row.service, row.component].filter(Boolean).join(' · ') || '—'} />
                <DField label="Occurrences" value={row.occurrence_count} />
                <DField label="First seen" value={formatDateTime(row.created_at)} />
                <DField label="Last seen" value={formatDateTime(row.last_seen)} />
              </>
            )}
            <DField label="Acknowledged by" value={row.acknowledged ? (row.acknowledged_by_name || row.acknowledged_by || 'Yes') : '—'} />
            <DField label="Assignee" value={assignee} />
            {row.resolved_by_name && <DField label="Resolved by" value={row.resolved_by_name} />}
            {row.cancelled && <DField label="Cancelled by" value={row.cancelled_by_name || 'Yes'} />}
            {row.cancel_reason && <DField label="Cancel reason" value={row.cancel_reason} />}
          </dl>

          <div className="border-t border-border pt-4">
            <ActivityFeed source={source} alertId={row.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Small prompt for event resolve/cancel note ─────────────────
function NoteModal({ kind, onClose, onConfirm }: { kind: 'resolve' | 'cancel'; onClose: () => void; onConfirm: (text: string) => void }) {
  const [text, setText] = useState('');
  const title = kind === 'resolve' ? 'Resolve alert' : 'Cancel (dismiss) alert';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-foreground mb-3">{title}</h2>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={kind === 'resolve' ? 'Resolution note (optional)' : 'Reason (optional)'}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className={ghostBtnCls}>Cancel</button>
          <button onClick={() => onConfirm(text.trim())} className={primaryBtnCls}>Confirm</button>
        </div>
      </div>
    </div>
  );
}
